import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import { calculateShiftEndTime } from 'src/utils/shift';
import { showToastAction } from '../ui';
import {
    fetchDisputes,
    disputesFetched,
    disputesFetchFailed,
    resolveDispute,
    payAndResolveDispute,
    editDisputeTracker,
    disputeActionDone,
    selectDisputesState,
} from '../disputes';

const toast = (type, text) => put(showToastAction({ type, text }));

function* fetchDisputesEffect() {
    try {
        const { query } = yield select(selectDisputesState);
        const queryParams = { page: query.page, limit: query.limit };
        if (query.search) queryParams.search = query.search;
        if (query.province) queryParams.province = query.province;
        const data = yield call(SendApiRequest, {
            url: apiRoutes.disputes,
            method: 'GET',
            queryParams,
        });
        // The last page can empty out after a resolve; step back one page.
        if (!data.results.length && data.total > 0 && query.page > 1) {
            yield put(fetchDisputes({ page: query.page - 1 }));
            return;
        }
        yield put(disputesFetched(data));
    } catch (e) {
        yield put(disputesFetchFailed(e.message));
        yield toast('danger', e.message || 'Failed to load disputes');
    }
}

// Silent refresh keeps the current page/search/province.
const refresh = () => put(fetchDisputes({}));

function* resolveDisputeEffect({ payload }) {
    const { jobId, stafferId } = payload;
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.resolveDispute(jobId),
            method: 'POST',
            body: stafferId ? { stafferId } : {},
        });
        yield toast('success', 'Dispute resolved');
        yield refresh();
    } catch (e) {
        yield toast('danger', e.message || 'Failed to resolve dispute');
    } finally {
        yield put(disputeActionDone());
    }
}

function* payAndResolveEffect({ payload }) {
    const { jobId, invoiceId } = payload;
    let paid = false;
    try {
        // Same call Admin 1.0 makes for "Pay Invoice and resolve dispute".
        yield call(SendApiRequest, {
            url: apiRoutes.payInvoiceFromDispute(invoiceId),
            method: 'POST',
            queryParams: { actionTaken: 'admin', isDisputed: true },
        });
        paid = true;
        yield call(SendApiRequest, {
            url: apiRoutes.resolveDispute(jobId),
            method: 'POST',
            body: {},
        });
        yield toast('success', 'Invoice paid and dispute resolved');
        yield refresh();
    } catch (e) {
        if (paid) {
            yield toast(
                'warning',
                'Invoice was paid but the dispute could not be marked resolved. Use "Resolve" on it.',
            );
            yield refresh();
        } else {
            yield toast('danger', e.message || 'Failed to pay invoice');
        }
    } finally {
        yield put(disputeActionDone());
    }
}

// Finds a contractor's tracker in a job's shifts (staffer may be populated).
const findContractorTracker = (job, stafferId) => {
    if (!job || !Array.isArray(job.staffersAndShifts)) return null;
    const shift = job.staffersAndShifts.find(
        s =>
            s &&
            s.staffer &&
            String(s.staffer._id || s.staffer) === String(stafferId),
    );
    return shift ? shift.tracker : null;
};

/**
 * Mirrors Admin 1.0's editTrackerAndInvoiceFromDispute: update (or create) the
 * contractor's tracker so the shift runs start -> start + hh:mm:ss, then patch
 * the invoice with isDispute so it is re-priced.
 */
function* editDisputeTrackerEffect({ payload }) {
    const { job, dispute, duration } = payload;
    try {
        const stafferId = dispute.staffer._id;
        const start = job.shiftStartTime;
        // Admin 1.0 derives the end from the job's own timezone; shiftStartTime
        // is already an absolute unix timestamp so a plain addition is equivalent.
        const end = calculateShiftEndTime(start, duration);

        const res = yield call(SendApiRequest, {
            url: dispute.trackerId
                ? apiRoutes.updateTrackerV1
                : apiRoutes.createTrackerManually,
            method: dispute.trackerId ? 'PATCH' : 'POST',
            body: dispute.trackerId
                ? {
                      trackerId: dispute.trackerId,
                      startTime: start,
                      stopTime: end,
                  }
                : {
                      jobId: job._id,
                      userId: stafferId,
                      startTime: start,
                      stopTime: end,
                  },
        });

        const tracker =
            res && res.tracker && res.tracker.start
                ? res.tracker
                : findContractorTracker(res && res.job, stafferId);

        yield call(SendApiRequest, {
            url: apiRoutes.invoicesNewModel(dispute.invoiceId),
            method: 'PATCH',
            body: {
                tracker,
                stafferId:
                    tracker && typeof tracker.user === 'object'
                        ? tracker.user._id
                        : (tracker && tracker.user) || stafferId,
                isEditTracker: true,
                jobId: job._id,
                isDispute: true,
                isSendEmail: false,
            },
        });
        yield toast('success', 'Tracker and invoice updated');
        yield refresh();
    } catch (e) {
        yield toast(
            'danger',
            e.message === 'Employer has no credit card'
                ? e.message
                : e.message || 'Failed to edit tracker',
        );
    } finally {
        yield put(disputeActionDone());
    }
}

export const disputesSagas = [
    takeLatest(fetchDisputes.type, fetchDisputesEffect),
    takeEvery(resolveDispute.type, resolveDisputeEffect),
    takeEvery(payAndResolveDispute.type, payAndResolveEffect),
    takeEvery(editDisputeTracker.type, editDisputeTrackerEffect),
];
